import React from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/primitives/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@repo/ui/primitives/sidebar";
import { Popover, PopoverTrigger, PopoverContent } from "@repo/ui/primitives/popover";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useHelperTourStore } from "@/store/helperTour.store";
import { useSidebarPreferencesStore } from "@/store/sidebar-preferences.store";
import { prefetchNamespacesForPath } from "@/lib/i18n-route-namespaces.js";
import { cn } from "@/lib/utils";

function isUnmodifiedLeftClick(event) {
  return event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey;
}

function bindLocalePrefetch(url) {
  return {
    onMouseEnter: () => {
      void prefetchNamespacesForPath(url);
    },
    onFocus: () => {
      void prefetchNamespacesForPath(url);
    },
  };
}

function NavIcon({ icon, isActive }) {
  return React.createElement(icon, {
    className: cn(
"size-4 shrink-0",
      isActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70",
    ),
"aria-hidden": true,
  });
}

function LeafNavItem({
  item,
  isCollapsed,
  isParentActive,
  isSidebarTabsStep,
  handlePageClick,
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={isCollapsed ? item.title : undefined}
        isActive={isParentActive(item)}
        className="cursor-pointer"
        data-helper-module={item.helperKey || undefined}
        data-helper={item.dataHelper || undefined}
      >
        <Link
          to={item.url}
          {...bindLocalePrefetch(item.url)}
          onClick={(e) => {
            if (isSidebarTabsStep && item.helperKey) {
              e.preventDefault();
              return;
            }
            if (isUnmodifiedLeftClick(e)) {
              handlePageClick(item.url);
            }
          }}
        >
          <NavIcon icon={item.icon} isActive={isParentActive(item)} />
          {!isCollapsed && <span>{item.title}</span>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function SubNavItems({
  item,
  isSubMenuOpen,
  handleSubOpenChange,
  isSubBranchActive,
  isActive,
  handlePageClick,
  showIndicators,
}) {
  return item.items.map((subItem) => (
    <SidebarMenuSubItem key={subItem.id}>
      {subItem.items ? (
        <Collapsible
          open={isSubMenuOpen(item.id, subItem.id)}
          onOpenChange={handleSubOpenChange(subItem.id)}
          className="group/collapsible-sub"
        >
          <CollapsibleTrigger asChild>
            <SidebarMenuSubButton
              asChild
              isActive={isSubBranchActive(item.id, subItem)}
              className="w-full cursor-pointer"
              data-helper={subItem.dataHelper || undefined}
            >
              <button type="button">
                <span className="min-w-0 flex-1 text-left">{subItem.title}</span>
                {showIndicators && (
                  <ChevronRight
                    className="ml-auto size-3.5 shrink-0 text-sidebar-foreground/70 transition-transform duration-200 group-data-[state=open]/collapsible-sub:rotate-90"
                  />
                )}
              </button>
            </SidebarMenuSubButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {subItem.items.map((subSubItem) => (
                <SidebarMenuSubItem key={subSubItem.id}>
                  <SidebarMenuSubButton
                    asChild
                    size="sm"
                    isActive={isActive(
                      subSubItem.url,
                      subSubItem.exact,
                      subSubItem.activePathPrefixes,
                      subSubItem.excludePathPrefixes,
                      subSubItem.activeSearchParams,
                      subSubItem.activeSearchParamsAllowMissing,
                    )}
                    data-helper={subSubItem.dataHelper || undefined}
                  >
                    <Link
                      to={subSubItem.url}
                      {...bindLocalePrefetch(subSubItem.url)}
                      onClick={(event) => {
                        if (isUnmodifiedLeftClick(event)) {
                          handlePageClick(subSubItem.url);
                        }
                      }}
                    >
                      <span>{subSubItem.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <SidebarMenuSubButton
          asChild
          isActive={isActive(
            subItem.url,
            subItem.exact,
            subItem.activePathPrefixes,
            subItem.excludePathPrefixes,
            subItem.activeSearchParams,
            subItem.activeSearchParamsAllowMissing,
          )}
          data-helper={subItem.dataHelper || undefined}
        >
          <Link
            to={subItem.url}
            {...bindLocalePrefetch(subItem.url)}
            onClick={(event) => {
              if (isUnmodifiedLeftClick(event)) {
                handlePageClick(subItem.url);
              }
            }}
          >
            <span>{subItem.title}</span>
          </Link>
        </SidebarMenuSubButton>
      )}
    </SidebarMenuSubItem>
  ));
}

function ParentNavItem({
  item,
  isCollapsed,
  navigate,
  isMenuOpen,
  toggleMainMenu,
  isSubMenuOpen,
  toggleSubMenu,
  isParentActive,
  isSubBranchActive,
  isActive,
  handlePageClick,
  isSidebarTabsStep,
  collapsedOpenItem,
  setCollapsedOpenItem,
  showIndicators,
  openOnHover,
}) {
  const handleMainOpenChange = (open) => {
    const currentlyOpen = isMenuOpen(item.id);
    if (open !== currentlyOpen) {
      toggleMainMenu(item.id);
    }
  };

  const handleSubOpenChange = (subItemId) => (open) => {
    const currentlyOpen = isSubMenuOpen(item.id, subItemId);
    if (open !== currentlyOpen) {
      toggleSubMenu(item.id, subItemId);
    }
  };

  return (
    <Collapsible
      open={isMenuOpen(item.id)}
      onOpenChange={handleMainOpenChange}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        {isCollapsed ? (
          <Popover
            open={collapsedOpenItem === item.id}
            onOpenChange={(open) => setCollapsedOpenItem(open ? item.id : null)}
          >
            <PopoverTrigger
              asChild
              onMouseEnter={() => {
                if (openOnHover) setCollapsedOpenItem(item.id);
              }}
            >
              <SidebarMenuButton
                isActive={isParentActive(item)}
                aria-label={item.title}
                className="cursor-pointer"
                data-helper-module={item.helperKey || undefined}
                data-helper={item.dataHelper || undefined}
              >
                <NavIcon icon={item.icon} isActive={isParentActive(item)} />
              </SidebarMenuButton>
            </PopoverTrigger>
            <PopoverContent
              side="right"
              align="start"
              collisionPadding={8}
              aria-label={item.title}
              className="z-[var(--ds-layer-transient)] ml-1 flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-[var(--ds-sidebar-width)] flex-col border-sidebar-border bg-popover p-2"
              onMouseEnter={() => {
                if (openOnHover) setCollapsedOpenItem(item.id);
              }}
              onMouseLeave={() => {
                if (openOnHover) setCollapsedOpenItem(null);
              }}
            >
              <div className="shrink-0 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {item.title}
              </div>
              <div className={cn("mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1", item.submenuClassName)}>
                <SidebarMenu>
                  <SubNavItems
                    item={item}
                    isSubMenuOpen={isSubMenuOpen}
                    handleSubOpenChange={handleSubOpenChange}
                    isSubBranchActive={isSubBranchActive}
                    isActive={isActive}
                    handlePageClick={(url) => {
                      handlePageClick(url);
                      setCollapsedOpenItem(null);
                    }}
                    showIndicators={showIndicators}
                  />
                </SidebarMenu>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              isActive={isParentActive(item)}
              onClick={() => {
                if (isSidebarTabsStep && item.helperKey) return;
                if (item.url !== "#") {
                  navigate(item.url);
                  handlePageClick(item.url);
                }
              }}
              className="cursor-pointer"
              data-helper-module={item.helperKey || undefined}
              data-helper={item.dataHelper || undefined}
            >
              <NavIcon icon={item.icon} isActive={isParentActive(item)} />
              <span className="min-w-0 flex-1 text-left">{item.title}</span>
              {showIndicators && (
                <ChevronRight
                  className="ml-auto size-4 shrink-0 text-sidebar-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                />
              )}
            </SidebarMenuButton>
          </CollapsibleTrigger>
        )}

        {!isCollapsed && (
          <CollapsibleContent>
            <SidebarMenuSub className={item.submenuClassName}>
              <SubNavItems
                item={item}
                isSubMenuOpen={isSubMenuOpen}
                handleSubOpenChange={handleSubOpenChange}
                isSubBranchActive={isSubBranchActive}
                isActive={isActive}
                handlePageClick={handlePageClick}
                showIndicators={showIndicators}
              />
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SidebarNavItems({
  items,
  isCollapsed,
  navigate,
  isMenuOpen,
  toggleMainMenu,
  isSubMenuOpen,
  toggleSubMenu,
  isParentActive,
  isSubBranchActive,
  isActive,
  handlePageClick,
  isSidebarTabsStep,
  collapsedOpenItem,
  setCollapsedOpenItem,
}) {
  const { showIndicators, openOnHover } = useSidebarPreferencesStore();

  return items.map((item) =>
    item.items ? (
      <ParentNavItem
        key={item.id}
        item={item}
        isCollapsed={isCollapsed}
        navigate={navigate}
        isMenuOpen={isMenuOpen}
        toggleMainMenu={toggleMainMenu}
        isSubMenuOpen={isSubMenuOpen}
        toggleSubMenu={toggleSubMenu}
        isParentActive={isParentActive}
        isSubBranchActive={isSubBranchActive}
        isActive={isActive}
        handlePageClick={handlePageClick}
        isSidebarTabsStep={isSidebarTabsStep}
        collapsedOpenItem={collapsedOpenItem}
        setCollapsedOpenItem={setCollapsedOpenItem}
        showIndicators={showIndicators}
        openOnHover={openOnHover}
      />
    ) : (
      <LeafNavItem
        key={item.id}
        item={item}
        isCollapsed={isCollapsed}
        isParentActive={isParentActive}
        isSidebarTabsStep={isSidebarTabsStep}
        handlePageClick={handlePageClick}
      />
    ),
  );
}

export function SidebarMenuSection({
  navGroups,
  isCollapsed,
  navigate,
  isMenuOpen,
  toggleMainMenu,
  isSubMenuOpen,
  toggleSubMenu,
  isParentActive,
  isSubBranchActive,
  isActive,
  handlePageClick,
}) {
  const { isOpen: isHelperOpen, steps, currentStepIndex } = useHelperTourStore();
  const isSidebarTabsStep =
    isHelperOpen && steps?.[currentStepIndex]?.id === "sidebar-tabs";

  const [collapsedOpenItem, setCollapsedOpenItem] = React.useState(null);

  const sharedProps = {
    isCollapsed,
    navigate,
    isMenuOpen,
    toggleMainMenu,
    isSubMenuOpen,
    toggleSubMenu,
    isParentActive,
    isSubBranchActive,
    isActive,
    handlePageClick,
    isSidebarTabsStep,
    collapsedOpenItem,
    setCollapsedOpenItem,
  };

  return (
    <>
      {navGroups.map((group) => (
        <SidebarGroup key={group.id}>
          {!isCollapsed && group.id !== "quickAccess" && group.label && (
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarNavItems items={group.items} {...sharedProps} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  );
}
